<template>
  <div>
    <CCard>
      <CCardHeader>
        <h5 class="mb-0">{{ $pgettext('tag_definitions.form', 'Basic Information') }}</h5>
      </CCardHeader>
      <CCardBody>
        <CRow class="mb-3">
          <CCol sm="6">
            <label>{{ $pgettext('tag_definitions.form', 'Name *') }}</label>
            <CInput
                v-model="form.name"
                :placeholder="$pgettext('tag_definitions.placeholder', 'Tag name')"
            />
            <small class="text-muted">{{ $pgettext('tag_definitions.help', 'Display name for this tag') }}</small>
          </CCol>
          <CCol sm="6">
            <label>{{ $pgettext('tag_definitions.form', 'Code *') }}</label>
            <CInput
                v-model="form.code"
                :placeholder="$pgettext('tag_definitions.placeholder', 'tag_code')"
            />
            <small class="text-muted">{{ $pgettext('tag_definitions.help', 'Unique identifier (lowercase, underscore separator)') }}</small>
          </CCol>
        </CRow>
        <CRow class="mb-3">
          <CCol sm="12">
            <label>{{ $pgettext('tag_definitions.form', 'Description') }}</label>
            <CTextarea
                v-model="form.description"
                :placeholder="$pgettext('tag_definitions.placeholder', 'Optional description')"
                rows="3"
            />
          </CCol>
        </CRow>
        <CRow class="mb-3">
          <CCol sm="12">
            <div class="custom-control custom-checkbox">
              <input
                  type="checkbox"
                  class="custom-control-input"
                  id="is-assignable"
                  v-model="form.is_assignable"
              />
              <label class="custom-control-label" for="is-assignable">
                <span>{{ $pgettext('tag_definitions.form', 'Manually Assignable') }}</span>
                <br>
                <small class="text-muted">{{ $pgettext('tag_definitions.help', 'Can be manually assigned by users') }}</small>
              </label>
            </div>
          </CCol>
        </CRow>
        <CRow class="mb-3">
          <CCol sm="12">
            <div class="custom-control custom-checkbox">
              <input
                  type="checkbox"
                  class="custom-control-input"
                  id="is-enabled"
                  v-model="form.is_enabled"
              />
              <label class="custom-control-label" for="is-enabled">
                <span>{{ $pgettext('tag_definitions.form', 'Enabled') }}</span>
                <br>
                <small class="text-muted">{{ $pgettext('tag_definitions.help', 'Tag definition is active and can be used') }}</small>
              </label>
            </div>
          </CCol>
        </CRow>
        <CRow class="mb-3">
          <CCol sm="12">
            <div class="custom-control custom-checkbox">
              <input
                  type="checkbox"
                  class="custom-control-input"
                  id="is-required"
                  v-model="form.is_required"
              />
              <label class="custom-control-label" for="is-required">
                <span>{{ $pgettext('tag_definitions.form', 'Required') }}</span>
                <br>
                <small class="text-muted">{{ $pgettext('tag_definitions.help', 'This tag must be filled for completion status') }}</small>
              </label>
            </div>
          </CCol>
        </CRow>
        <CRow class="mb-3">
          <CCol sm="6">
            <label>{{ $pgettext('tag_definitions.form', 'Display Order') }}</label>
            <CInput
                v-model.number="form.order"
                type="number"
                min="0"
                :placeholder="$pgettext('tag_definitions.placeholder', '0')"
            />
            <small class="text-muted">{{ $pgettext('tag_definitions.help', 'Order for displaying tags (lower numbers appear first)') }}</small>
          </CCol>
        </CRow>
        <CRow class="mb-3" v-if="canManageContractTags">
          <CCol sm="12">
            <div class="custom-control custom-checkbox">
              <input
                  type="checkbox"
                  class="custom-control-input"
                  id="is-contract-level"
                  v-model="form.is_contract_level"
                  :disabled="isEditMode && data.is_contract_level"
              />
              <label class="custom-control-label" for="is-contract-level">
                <span>{{ $pgettext('tag_definitions.form', 'Create as Contract-Level Tag') }}</span>
                <br>
                <small class="text-muted">{{ $pgettext('tag_definitions.help', 'This tag will be shared across all events in the contract') }}</small>
              </label>
            </div>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CAlert color="warning" class="mt-4" v-if="form.is_contract_level">
      <div class="d-flex align-items-center">
        <span class="mr-2" style="font-size: 1.5em;">⚠️</span>
        <div>
          <strong>{{ $pgettext('tag_definitions.warning', 'Contract-Level Tag') }}</strong>
          <p class="mb-0 mt-1">
            {{ $pgettext('tag_definitions.warning', 'This tag will be shared across all events in the contract. Any changes will affect all events.') }}
          </p>
        </div>
      </div>
    </CAlert>

    <CCard class="mt-4">
      <CCardHeader>
        <h5 class="mb-0">{{ $pgettext('tag_definitions.form', 'Input Configuration') }}</h5>
      </CCardHeader>
      <CCardBody>
        <CRow class="mb-3">
          <CCol sm="12">
            <label>{{ $pgettext('tag_definitions.form', 'Input Type') }}</label>
            <CSelect
                v-model="form.input_config.type"
                @update:value="onInputTypeChange"
                :options="inputTypeOptions"
            />
          </CCol>
        </CRow>

        <TextInputConfig
            v-if="form.input_config.type === 'text'"
            ref="inputConfig"
        />
        <SelectInputConfig
            v-else-if="form.input_config.type === 'select'"
            ref="inputConfig"
        />
        <MultiselectInputConfig
            v-else-if="form.input_config.type === 'multiselect'"
            ref="inputConfig"
        />
        <CheckboxInputConfig
            v-else-if="form.input_config.type === 'checkbox'"
            ref="inputConfig"
        />
        <NumberInputConfig
            v-else-if="form.input_config.type === 'number'"
            ref="inputConfig"
        />
        <DateInputConfig
            v-else-if="form.input_config.type === 'date'"
            ref="inputConfig"
        />
        <TagsInputConfig
            v-else-if="form.input_config.type === 'tags'"
            ref="inputConfig"
        />
      </CCardBody>
    </CCard>

    <CCard class="mt-4">
      <CCardHeader>
        <h5 class="mb-0">{{ $pgettext('tag_definitions.form', 'Scope Configuration') }}</h5>
      </CCardHeader>
      <CCardBody>
        <CRow>
          <CCol sm="12">
            <label class="font-weight-medium mb-3">{{ $pgettext('tag_definitions.form', 'Tag Applicability Scopes') }}</label>
            <p class="text-muted small">{{ $pgettext('tag_definitions.form', 'Select where this tag can be applied. At least one scope must be selected.') }}</p>
          </CCol>
        </CRow>
        <CRow>
          <CCol sm="6" md="4" lg="3" class="mb-3" v-for="scope in availableScopes" :key="scope.key">
            <div class="custom-control custom-checkbox">
              <input
                  type="checkbox"
                  class="custom-control-input"
                  :id="`scope-${scope.key}`"
                  v-model="form[scope.key]"
              />
              <label class="custom-control-label" :for="`scope-${scope.key}`">
                <span class="font-weight-medium">{{ scope.label }}</span>
                <br>
                <small class="text-muted">{{ scope.description }}</small>
              </label>
            </div>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard class="mt-4">
      <CCardFooter>
        <div class="d-flex justify-content-end">
          <CButton color="secondary" variant="outline" @click="onReset" class="mr-2">
            {{ $pgettext('tag_definitions.form', 'Reset') }}
          </CButton>
          <CButton color="primary" @click="onSave">
            {{ $pgettext('tag_definitions.form', 'Save') }}
          </CButton>
        </div>
      </CCardFooter>
    </CCard>
  </div>
</template>

<script>
import {getAllTagScopes} from "@/domain/core/constant/tagScope";
import TextInputConfig from "@/domain/tagDefinitions/components/inputTypes/TextInputConfig.vue";
import SelectInputConfig from "@/domain/tagDefinitions/components/inputTypes/SelectInputConfig.vue";
import MultiselectInputConfig from "@/domain/tagDefinitions/components/inputTypes/MultiselectInputConfig.vue";
import CheckboxInputConfig from "@/domain/tagDefinitions/components/inputTypes/CheckboxInputConfig.vue";
import NumberInputConfig from "@/domain/tagDefinitions/components/inputTypes/NumberInputConfig.vue";
import DateInputConfig from "@/domain/tagDefinitions/components/inputTypes/DateInputConfig.vue";
import TagsInputConfig from "@/domain/tagDefinitions/components/inputTypes/TagsInputConfig.vue";

export default {
  name: "TagDefinitionForm",
  components: {
    TextInputConfig,
    SelectInputConfig,
    MultiselectInputConfig,
    CheckboxInputConfig,
    NumberInputConfig,
    DateInputConfig,
    TagsInputConfig
  },
  props: {
    data: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      form: {
        code: '',
        name: '',
        description: '',
        input_config: {
          type: 'text',
          placeholder: '',
          max_length: null
        },
        allow_media: false,
        allow_cluster: false,
        is_assignable: true,
        is_enabled: true,
        is_required: false,
        is_contract_level: false,
        order: 0
      },
      inputTypeOptions: [
        {value: 'text', label: this.$pgettext('tag_definitions.input_type', 'Text')},
        {value: 'select', label: this.$pgettext('tag_definitions.input_type', 'Select')},
        {value: 'multiselect', label: this.$pgettext('tag_definitions.input_type', 'Multi Select')},
        {value: 'checkbox', label: this.$pgettext('tag_definitions.input_type', 'Checkbox')},
        {value: 'number', label: this.$pgettext('tag_definitions.input_type', 'Number')},
        {value: 'date', label: this.$pgettext('tag_definitions.input_type', 'Date')},
        {value: 'tags', label: this.$pgettext('tag_definitions.input_type', 'Tags')}
      ],
      availableScopes: getAllTagScopes().map(scope => ({
        key: `allow_${scope.value}`,
        label: scope.label,
        description: scope.description
      }))
    }
  },
  computed: {
    isCodeValid() {
      if (!this.form.code) return null
      return /^[a-z][a-z0-9_]*$/.test(this.form.code)
    },
    isEditMode() {
      return !!this.data.id
    },
    canManageContractTags() {
      return this.hasPerm('tag_definition.manage_contract_tags')
    }
  },
  mounted() {
    this.onReset()
  },
  watch: {
    'form.name'(newName) {
      if (this.isEditMode) return
      this.form.code = this.generateCodeFromName(newName)
    }
  },
  methods: {
    generateCodeFromName(name) {
      if (!name) return ''
      return name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s_-]/g, '') // Remove special chars except spaces, underscore, dash
        .replace(/[\s-]+/g, '_') // Replace spaces and dashes with underscore
        .replace(/_+/g, '_') // Replace multiple underscores with single
        .replace(/^_|_$/g, '') // Remove leading/trailing underscores
    },
    async onReset() {
      this.form = {
        code: '',
        name: '',
        description: '',
        input_config: {
          type: 'text',
          placeholder: '',
          max_length: null
        },
        allow_media: false,
        allow_cluster: false,
        is_assignable: true,
        is_enabled: true,
        is_required: false,
        is_contract_level: false,
        order: 0,
        ...this.data
      }

      await this.$nextTick()
      if (this.$refs.inputConfig && this.$refs.inputConfig.onReset) {
        this.$refs.inputConfig.onReset(this.form.input_config)
      }
    },
    async onInputTypeChange(type) {
      let newConfig = {type}

      switch (type) {
        case 'text':
          newConfig = {type, placeholder: '', max_length: null}
          break
        case 'select':
          newConfig = {type, options: [{value: '', label: ''}]}
          break
        case 'multiselect':
          newConfig = {type, options: [{value: '', label: ''}]}
          break
        case 'checkbox':
          newConfig = {type, options: [{value: '', label: ''}]}
          break
        case 'number':
          newConfig = {type, min: null, max: null, step: 1}
          break
        case 'date':
          newConfig = {type, min_date: null, max_date: null}
          break
        case 'tags':
          newConfig = {type}
          break
      }

      this.form.input_config = newConfig

      await this.$nextTick()
      if (this.$refs.inputConfig && this.$refs.inputConfig.onReset) {
        this.$refs.inputConfig.onReset(newConfig)
      }
    },
    onSave() {
      // Raccoglie i dati dal componente figlio prima di emettere save
      if (this.$refs.inputConfig && this.$refs.inputConfig.onSave) {
        this.form.input_config = this.$refs.inputConfig.onSave()
      }
      this.$emit('save', this.form)
    }
  }
}
</script>

<style scoped>
label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.custom-control-label {
  cursor: pointer;
}
</style>
